import { ValueOf } from '@/common/common.types';
import { pluralize } from '@/common/utils/app.utils';
import { theme } from '@/core/theme/theme';
import {
  GetUnacceptedBatchesResponse,
  SummarizeMinimizedResponse,
} from '@/dashboard/services/dashboardApi';
import {
  categorizeErrorCountsFromSummarize,
  processNumericSummarizeValues,
} from '@/dashboard/utils/dashboard.utils';

export const DASHBOARD_BAND_COLOURS = {
  PRE_SUBMIT_ERRORS: theme.palette.error.main,
  POST_SUBMIT_ERRORS: theme.palette.error.main,
  CRITICAL_ERRORS: theme.palette.error.main,
  WARNINGS: theme.palette.custom.warningYellow as string,
  BYPASSED_ERRORS: theme.palette.warning.light,
  UNSUBMITTED: theme.palette.secondary.main,
  ACCEPTED: theme.palette.success.main,
  VALIDATED: theme.palette.info.light,
};

const DASHBOARD_BAND_LABELS: Partial<typeof DASHBOARD_BAND_COLOURS> = {
  PRE_SUBMIT_ERRORS: 'Errors',
  POST_SUBMIT_ERRORS: 'Post-Submit Errors',
  CRITICAL_ERRORS: 'Critical Errors',
  WARNINGS: 'Warnings',
};

function buildCountsForDashboardBands(count: number, type: keyof typeof DASHBOARD_BAND_LABELS) {
  const filtered = processNumericSummarizeValues(count);
  const formatted = Number(filtered).toLocaleString('en-US');

  return `${DASHBOARD_BAND_LABELS[type]} (${formatted})`;
}

export type DashboardColouredSectionTypes = 'unsubmitted' | 'submitted' | 'error' | 'warning';
export type DashboardBandsLabel =
  | 'No records found'
  | 'Attention required'
  | 'Validated'
  | 'Accepted'
  | 'Bypass approved'
  | 'Rejected';
export type DashboardBandSectionsType = {
  colour: string;
  contents: string;
  type: DashboardColouredSectionTypes;
  tooltip: string;
  fontColour: 'white' | 'text.primary';
}[];
export type DashboardRowPeriodDetails = Record<
  string,
  {
    period: string;
    label: DashboardBandsLabel;
    buttons: {
      upload: boolean | null;
      validate: boolean | null;
      submit: boolean | null;
    };
    bands: {
      errors: number;
      criticalErrors: number;
      postSubmitErrors: number;
      warnings: number;
      unsubmitted: number;
      submitted: number;
    };
    bandSections: DashboardBandSectionsType;
  }
>;

export function initializePeriodCalculations(period: string) {
  return {
    period,
    label: 'No records found',
    buttons: {
      upload: null,
      validate: null,
      submit: null,
    },
    bands: {
      errors: 0,
      criticalErrors: 0,
      postSubmitErrors: 0,
      warnings: 0,
      unsubmitted: 0,
      submitted: 0,
    },
    bandSections: [],
  } as ValueOf<DashboardRowPeriodDetails>;
}

export function hideAllButtons() {
  return {
    submit: null,
    upload: null,
    validate: null,
  };
}

export function isSubmitActive(
  counts: ValueOf<SummarizeMinimizedResponse>,
  unacceptedBatches: GetUnacceptedBatchesResponse,
  period: string,
) {
  const categorizedErrors = categorizeErrorCountsFromSummarize(counts);
  const activeSubmitPeriod = unacceptedBatches?.nextSubmissionBatch?.period;
  const isErrors = categorizedErrors.preSubmitErrors;
  const isBypassed = counts.bypassTimestamp !== null;
  const isSubmittable = unacceptedBatches.nextSubmissionBatch?.submitStatus.submittable === true;

  if (period === activeSubmitPeriod && isBypassed) {
    return true;
  }

  if (period === activeSubmitPeriod && isErrors === 0 && isSubmittable) {
    return true;
  }

  return false;
}

export function getLabelStatusOfBatch(
  counts: ValueOf<SummarizeMinimizedResponse>,
): DashboardBandsLabel | undefined {
  const totalEmployees = counts.totals.employeeRecords;

  if (counts.latestBatchStatus === 'A') {
    return 'Accepted';
  }

  if (totalEmployees === 0) {
    return 'No records found';
  }

  if (totalEmployees > 0) {
    if (counts.lastSubmitStatus === 'E' && counts.lastSubmission !== null) {
      return 'Rejected';
    }
    return 'Attention required';
  }
}

export function getErrorsFromSummarize(counts: ValueOf<SummarizeMinimizedResponse>) {
  const categorizedErrors = categorizeErrorCountsFromSummarize(counts);

  return {
    errors: categorizedErrors.preSubmitErrors,
    criticalErrors: categorizedErrors.criticalErrors,
    postSubmitErrors: categorizedErrors.postSubmitErrors,
    warnings: categorizedErrors.warnings,
  };
}

export const dashboardBandSectionBuilder = {
  buildUnsubmittedSection(
    unsubmitted: number,
    totalEmployees: number,
    submitted: number,
    isSubmitActive: boolean,
  ) {
    if (unsubmitted > 0) {
      return {
        colour: isSubmitActive
          ? DASHBOARD_BAND_COLOURS.VALIDATED
          : DASHBOARD_BAND_COLOURS.UNSUBMITTED,
        contents: `${unsubmitted} Unsubmitted`,
        type: 'unsubmitted',
        tooltip: `${unsubmitted} Unsubmitted`,
        fontColour: 'black',
      };
    } else if (totalEmployees > 0 && submitted === 0 && unsubmitted === 0) {
      return {
        colour: DASHBOARD_BAND_COLOURS.UNSUBMITTED,
        contents: `${totalEmployees} ${pluralize('Employee', totalEmployees)}`,
        type: 'unsubmitted',
        tooltip: `${totalEmployees} ${pluralize('Employee', totalEmployees)}`,
        fontColour: 'black',
      };
    }
  },
  buildAcceptedSection(submitted: number, postSubmitErrors: number) {
    const accepted = submitted - postSubmitErrors;
    if (accepted > 0) {
      return {
        colour: DASHBOARD_BAND_COLOURS.ACCEPTED,
        contents: `${accepted} Accepted`,
        type: 'submitted',
        tooltip: `${accepted} Accepted`,
        fontColour: 'black',
      };
    }
  },
  buildPostsubmitErrorSection(postSubmitErrors: number, isBypassApproved: boolean) {
    if (postSubmitErrors > 0) {
      const labelMain = isBypassApproved ? 'Bypassed Post-Submit Error' : 'Post-Submit Error';
      return {
        colour: isBypassApproved
          ? DASHBOARD_BAND_COLOURS.BYPASSED_ERRORS
          : DASHBOARD_BAND_COLOURS.POST_SUBMIT_ERRORS,
        contents: buildCountsForDashboardBands(postSubmitErrors, 'POST_SUBMIT_ERRORS'),
        type: 'error',
        tooltip: `${postSubmitErrors} ${pluralize(labelMain, postSubmitErrors)}`,
        fontColour: isBypassApproved ? 'text.primary' : 'common.white',
      };
    }
  },
  buildCriticalErrorSection(criticalErrors: number, isBypassApproved: boolean) {
    if (criticalErrors > 0) {
      return {
        colour: isBypassApproved
          ? DASHBOARD_BAND_COLOURS.BYPASSED_ERRORS
          : DASHBOARD_BAND_COLOURS.POST_SUBMIT_ERRORS,
        contents: buildCountsForDashboardBands(criticalErrors, 'CRITICAL_ERRORS'),
        type: 'error',
        tooltip: `${criticalErrors} ${pluralize('Critical Error', criticalErrors)}`,
        fontColour: isBypassApproved ? 'text.primary' : 'common.white',
      };
    }
  },
  buildPresubmitErrorSection(preSubmitErrors: number, isBypassApproved: boolean) {
    if (preSubmitErrors > 0) {
      const labelMain = isBypassApproved ? 'Bypassed Error' : 'Error';

      return {
        colour: isBypassApproved
          ? DASHBOARD_BAND_COLOURS.BYPASSED_ERRORS
          : DASHBOARD_BAND_COLOURS.POST_SUBMIT_ERRORS,
        contents: buildCountsForDashboardBands(preSubmitErrors, 'PRE_SUBMIT_ERRORS'),
        type: 'error',
        tooltip: `${preSubmitErrors} ${pluralize(labelMain, preSubmitErrors)}`,
        fontColour: isBypassApproved ? 'text.primary' : 'common.white',
      };
    }
  },
  buildWarningsSection(warnings: number, isBypassApproved: boolean) {
    if (warnings > 0) {
      const labelMain = isBypassApproved ? 'Bypassed Warning' : 'Warning';

      return {
        colour: DASHBOARD_BAND_COLOURS.WARNINGS,
        contents: buildCountsForDashboardBands(warnings, 'WARNINGS'),
        type: 'warning',
        tooltip: `${warnings} ${pluralize(labelMain, warnings)}`,
        fontColour: 'text.primary',
      };
    }
  },
};
